import { useState } from "react";
import { Checkbox, FormControlLabel, Grid } from "@material-ui/core";
import { GeneralModal } from ".";
import {
  ButtonWithLoadingIndicator,
  CancelButton,
  TextFieldInput,
} from "../components-atoms";
import { ITheme } from "../constants/theme";
import { makeStyles } from "@material-ui/styles";

interface IProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (data: InviteMerchantUserData) => void;
  loading?: boolean;
}

export interface InviteMerchantUserData {
  FirstName: string;
  LastName: string;
  Email: string;
  PhoneNumber?: string;
  ShouldSendEmailNotification: boolean;
  ShouldSendNotification: boolean;
}

export const InviteMerchantUserModal = ({
  visible,
  onClose,
  onSubmit,
  loading = false
}: IProps) => {
  const classes = useStyles();
  const [formData, setFormData] = useState<InviteMerchantUserData>({
    FirstName: "",
    LastName: "",
    Email: "",
    PhoneNumber: "",
    ShouldSendEmailNotification: false,
    ShouldSendNotification: false,
  });

  const handleInputChange = (field: keyof InviteMerchantUserData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.type === 'checkbox' 
      ? event.target.checked 
      : event.target.value;
    
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const onFormSubmit = () => {
    onSubmit(formData);
    handleClose();
  };

  const handleClose = () => {
    // Reset form data
    setFormData({
      FirstName: "",
      LastName: "",
      Email: "",
      PhoneNumber: "",
      ShouldSendEmailNotification: false,
      ShouldSendNotification: false,
    });
    onClose();
  };

  const isFormValid = () => {
    return (
      !!formData.FirstName?.trim() &&
      !!formData.LastName?.trim() &&
      !!formData.Email?.trim() &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.Email || "")
    );
  };

  return (
    <GeneralModal
      title="Invite New Merchant User"
      visible={visible}
      onClose={handleClose}
    >
      <form>
        <GeneralModal.ContentArea>
          <fieldset className={classes.fieldset}>
            <Grid container spacing={3}>
              <Grid item xs={6}>
                <TextFieldInput
                  label="First Name"
                  value={formData.FirstName}
                  onChange={handleInputChange('FirstName')}
                  placeholder="Enter first name"
                  required
                />
              </Grid>
              
              <Grid item xs={6}>
                <TextFieldInput
                  label="Last Name"
                  value={formData.LastName}
                  onChange={handleInputChange('LastName')}
                  placeholder="Enter last name"
                  required
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextFieldInput
                  label="Email Address"
                  type="email"
                  value={formData.Email}
                  onChange={handleInputChange('Email')}
                  placeholder="Enter email address"
                  required
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextFieldInput
                  label="Phone Number"
                  value={formData.PhoneNumber || ""}
                  onChange={handleInputChange('PhoneNumber')}
                  placeholder="Enter phone number"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Checkbox color="primary" checked={formData.ShouldSendEmailNotification} onChange={handleInputChange('ShouldSendEmailNotification')} />}
                  label="Receive Email Notification"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Checkbox color="primary" checked={formData.ShouldSendNotification} onChange={handleInputChange('ShouldSendNotification')} />}
                  label="Receive SMS Notification"
                />
              </Grid>
            </Grid>
          </fieldset>
        </GeneralModal.ContentArea>
        
        <GeneralModal.ButtonArea>
          <CancelButton onClick={handleClose} />
          <ButtonWithLoadingIndicator
            onClick={onFormSubmit}
            disabled={loading || !isFormValid()}
            title="Add User"
            type="submit"
            loading={loading}
          />
        </GeneralModal.ButtonArea>
      </form>
    </GeneralModal>
  );
};

const useStyles = makeStyles((theme: ITheme) => ({
  fieldset: {
    margin: "16px 0 0",
    padding: 0,
    border: 0,
  },
}));
